import { ApiClient } from "@/api/v2/api";
import { Task } from "@/types/v2";

import { get, set } from "idb-keyval";
import { v4 as uuidv4 } from "uuid";
import { TasksRoutes } from "../tasks/routes";
import { flattenDetails } from "@/utils/v2";

interface QueueItem<T = Task> {
  id: string;
  item: T;
  attempts: number;
  createdAt: Date;
  type: "TASK";
}

const QUEUE_KEY = "taskQueue";
const MAX_RETRY_ATTEMPTS = 100;
const DEFAULT_INTERVAL = 36000;

let intervalId: number | null = null;
let isProcessing = false;

const getQueue = async (): Promise<QueueItem[]> => {
  try {
    return (await get(QUEUE_KEY)) || [];
  } catch (error) {
    console.error("Error getting queue:", error);
    return [];
  }
};

const removeFromQueue = async (itemId: string): Promise<void> => {
  try {
    const queue = await getQueue();
    const updatedQueue = queue.filter((item) => item.id !== itemId);
    await set(QUEUE_KEY, updatedQueue);

    if (updatedQueue.length === 0) {
      stopProcessing();
    }
  } catch (error) {
    console.error("Error removing from queue:", error);
  }
};

const processItem = async (queueItem: QueueItem): Promise<void> => {
  await ApiClient.patch(
    TasksRoutes.FINISH_TASK_BY_KEY.replace("{key}", queueItem.item.key),
    { task: flattenDetails(queueItem.item) }
  );
};

const handleProcessingError = async (queueItem: QueueItem): Promise<void> => {
  const queue = await getQueue();
  const updatedQueue = queue.map((item) =>
    item.id === queueItem.id ? { ...item, attempts: item.attempts + 1 } : item
  );
  await set(QUEUE_KEY, updatedQueue);
};

const stopProcessing = (): void => {
  if (intervalId) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
};

const processQueue = async (): Promise<void> => {
  if (isProcessing || !navigator.onLine) return;

  try {
    isProcessing = true;
    const queue = await getQueue();

    for (const queueItem of queue) {
      if (queueItem.attempts >= MAX_RETRY_ATTEMPTS) {
        await removeFromQueue(queueItem.id);
        continue;
      }

      try {
        await processItem(queueItem);
        await removeFromQueue(queueItem.id);
      } catch (error) {
        await handleProcessingError(queueItem);
      }
    }
  } finally {
    isProcessing = false;
  }
};

const startProcessing = (): void => {
  if (!intervalId && navigator.onLine) {
    intervalId = window.setInterval(processQueue, DEFAULT_INTERVAL);
  }
};

export const QueueService = {
  addToQueue: async <T>(item: T): Promise<void> => {
    try {
      const queue = await getQueue();
      const queueItem: QueueItem<T> = {
        id: uuidv4(),
        item,
        attempts: 0,
        createdAt: new Date(),
        type: "TASK",
      };

      await set(QUEUE_KEY, [...queue, queueItem]);
      startProcessing();
    } catch (error) {
      console.error("Error adding to queue:", error);
      throw new Error("Failed to add item to queue");
    }
  },

  setupNetworkListeners: (): void => {
    window.addEventListener("online", startProcessing);
    window.addEventListener("offline", stopProcessing);
  },
};

export default QueueService;
